/**
 * TimeRangeSelect
 *
 * The start/end pair shown in the assign popovers next to the counter (or
 * officer) picker.
 *
 * A highlight drawn with a finger is routinely a slot out — 15 minutes wide on
 * a phone is a few millimetres — so the range the popover is about to save has
 * to be correctable in the popover itself, without redrawing the highlight.
 *
 * Option values are slot indices, not the HHMM strings themselves: the slot is
 * what every caller ultimately converts back to, and picking it directly keeps
 * the end boundary (slot NUM_SLOTS, which is a later clock time than the shift
 * start) out of the start list and vice versa.
 */

import { generateTimeSlots, getStartHour, hmmmToSlot, hmmmToEndSlot, NUM_SLOTS } from "@/config/modeConfig";
import { clampSelectionSlots } from "@/utils/timeRange";

interface TimeRangeSelectProps {
  shiftType: string;
  /** Current range, HHMM. `end` is exclusive — the time the range stops at. */
  start: string;
  end: string;
  /** Called with the new HHMM pair whenever either end moves. */
  onChange: (start: string, end: string) => void;
  isMobile: boolean;
  /** Accent colour of the focused control — matches the popover's own accent. */
  accent?: string;
  disabled?: boolean;
}

function fmt(hhmm: string): string {
  return `${hhmm.slice(0, 2)}:${hhmm.slice(2)}`;
}

export function TimeRangeSelect({
  shiftType,
  start,
  end,
  onChange,
  isMobile,
  accent = "#2563eb",
  disabled = false,
}: TimeRangeSelectProps) {
  // Index === slot number: generateTimeSlots emits every boundary from slot 0
  // (shift start) to slot NUM_SLOTS (shift end) inclusive.
  const slots = generateTimeSlots(shiftType, 1);
  const startHour = getStartHour(shiftType);

  // Run the incoming pair through the same clamp the pickers use, so the two
  // selects always show a range that exists (at least one slot, inside the
  // shift) even if a caller hands over a degenerate one.
  const [startSlot, endSlot] = clampSelectionSlots(
    hmmmToSlot(start, startHour),
    hmmmToEndSlot(end, startHour),
    "end",
  );

  // Moving one end past the other carries the other along, so the range is
  // always at least the one slot the grid can actually hold.
  const emit = (moved: "start" | "end", s: number, e: number) => {
    const [nextStart, nextEnd] = clampSelectionSlots(s, e, moved);
    onChange(slots[nextStart], slots[nextEnd]);
  };

  const selectStyle: React.CSSProperties = {
    width: "100%",
    padding: isMobile ? "11px 10px" : "9px 10px",
    fontSize: isMobile ? 16 : 15,
    fontWeight: 600,
    borderRadius: 8,
    border: "1.5px solid #d1d5db",
    background: disabled ? "#f8fafc" : "white",
    color: disabled ? "#9ca3af" : "#1e293b",
    appearance: "auto",
    cursor: disabled ? "not-allowed" : "pointer",
    boxSizing: "border-box",
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 500,
    color: "#64748b",
    display: "block",
    marginBottom: 6,
  };

  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-end", marginBottom: 14 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <label style={labelStyle} htmlFor="assign-range-start">Start</label>
        <select
          id="assign-range-start"
          value={String(startSlot)}
          disabled={disabled}
          onChange={(e) => emit("start", parseInt(e.target.value, 10), endSlot)}
          style={selectStyle}
        >
          {slots.slice(0, NUM_SLOTS).map((t, i) => (
            <option key={`s${i}`} value={String(i)}>{fmt(t)}</option>
          ))}
        </select>
      </div>
      <span style={{ paddingBottom: isMobile ? 12 : 10, color: accent, fontWeight: 700, flexShrink: 0 }}>–</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <label style={labelStyle} htmlFor="assign-range-end">End</label>
        <select
          id="assign-range-end"
          value={String(endSlot)}
          disabled={disabled}
          onChange={(e) => emit("end", startSlot, parseInt(e.target.value, 10))}
          style={selectStyle}
        >
          {slots.slice(1).map((t, i) => (
            <option key={`e${i + 1}`} value={String(i + 1)}>{fmt(t)}</option>
          ))}
        </select>
      </div>
    </div>
  );
}
